import type { ReactNode } from 'react';
import { Link } from 'react-router';

const STEPS = [
  { label: 'Order confirmed', place: 'Jaipur', done: true },
  { label: 'Shipped', place: 'Jaipur hub', done: true },
  { label: 'In transit', place: 'Bhiwandi', done: true },
  { label: 'Out for delivery', place: 'Mumbai', current: true },
  { label: 'Delivered', place: '', done: false },
];

/** Split layout: the form on the right, and on the left the thing merchants are buying. */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="grid min-h-dvh lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
      <aside className="relative hidden overflow-hidden bg-brand-800 px-12 py-10 text-brand-50 lg:flex lg:flex-col">
        <Link to="/" className="flex items-center gap-2.5 text-lg font-semibold text-white">
          <img src="/favicon.svg" alt="" className="size-8" /> TrackFlow
        </Link>
        <div className="my-auto max-w-sm">
          <p className="text-3xl leading-tight font-semibold text-white">
            “Where is my order?” — answered before anyone asks.
          </p>
          <p className="mt-4 text-brand-100/80">
            A branded tracking page and proactive updates for every shipment, so your support inbox
            stays quiet.
          </p>
          <ol
            className="mt-10 space-y-0 rounded-(--radius-card) bg-white/5 p-5 ring-1 ring-white/10"
            aria-label="Example shipment timeline"
          >
            {STEPS.map((s, i) => (
              <li key={s.label} className="relative flex gap-3 pb-4 last:pb-0">
                {i < STEPS.length - 1 && (
                  <span
                    className={`absolute top-4 left-[7px] h-full w-0.5 ${s.done ? 'bg-signal-400' : 'bg-white/15'}`}
                    aria-hidden="true"
                  />
                )}
                <span
                  className={`relative mt-0.5 size-4 shrink-0 rounded-full border-2 ${
                    s.current
                      ? 'animate-pulse border-signal-400 bg-signal-400/30'
                      : s.done
                        ? 'border-signal-400 bg-signal-400'
                        : 'border-white/30'
                  }`}
                  aria-hidden="true"
                />
                <div className="text-sm">
                  <p
                    className={s.done || s.current ? 'font-medium text-white' : 'text-brand-100/60'}
                  >
                    {s.label}
                  </p>
                  {s.place && <p className="text-xs text-brand-100/60">{s.place}</p>}
                </div>
              </li>
            ))}
          </ol>
        </div>
        <p className="text-xs text-brand-100/50">
          For Shopify, WooCommerce and D2C brands · India & worldwide
        </p>
      </aside>

      <main className="flex flex-col justify-center px-4 py-10 sm:px-10">
        <div className="mx-auto w-full max-w-sm">
          <Link to="/" className="mb-8 flex items-center gap-2 font-semibold lg:hidden">
            <img src="/favicon.svg" alt="" className="size-7" /> TrackFlow
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          {subtitle && <p className="mt-1.5 text-sm text-ink-500">{subtitle}</p>}
          <div className="mt-8">{children}</div>
          {footer && <div className="mt-8 text-sm text-ink-600">{footer}</div>}
        </div>
      </main>
    </div>
  );
}

export function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p
      role="alert"
      className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
    >
      {message}
    </p>
  );
}
